import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import {
  collection,
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
  writeBatch,
  query,
  where,
  documentId,
  deleteField
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { useCollection } from 'vuefire'
import { firestore, functions } from '@/fb'
import type {
  Household,
  MealPlan,
  Pantry,
  Weekday,
  MealSlot,
  ShoppingListItem
} from '@/models/firestore'
import { getHouseholdIdsForQuery } from '@/utils/household'
import { useUserStore } from './userStore'

const WEEKDAYS: Weekday[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']

const generateId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
}

const generateInviteCode = (): string => {
  // Generate a short, readable invite code
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789' // Exclude confusing chars
  let code = ''
  for (let i = 0; i < 8; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return code
}

const createEmptyRecurringPlan = (): Record<Weekday, MealSlot[]> => {
  const plan: Record<Weekday, MealSlot[]> = {} as Record<Weekday, MealSlot[]>
  for (const day of WEEKDAYS) {
    plan[day] = []
  }
  return plan
}

export const useHouseholdStore = defineStore('household', () => {
  const userStore = useUserStore()

  // Computed query source - returns null when no households to query
  const householdsQuerySource = computed(() => {
    const householdIds = getHouseholdIdsForQuery(userStore.user)
    if (!householdIds || householdIds.length === 0) return null

    // Firestore 'in' queries are limited to 30 items, but users won't have that many households
    return query(
      collection(firestore, 'households'),
      where(documentId(), 'in', householdIds)
    )
  })

  // VueFire reactive binding - automatically subscribes/unsubscribes based on query
  const {
    data: householdsData,
    pending: householdsPending,
    error: householdsError
  } = useCollection<Household>(householdsQuerySource, { ssrKey: 'households' })

  // Map VueFire data to include document IDs
  const households = computed<Household[]>(() => {
    if (!householdsData.value) return []
    return householdsData.value.map(h => ({ ...h, id: h.id }))
  })

  // State
  const currentHouseholdId = ref<string | null>(null)
  const actionLoading = ref(false) // Loading state for actions (separate from VueFire pending)
  const actionError = ref<string | null>(null) // Error from actions (separate from VueFire query errors)

  // Combined loading/error (prefer action state when active)
  const loading = computed(() => actionLoading.value || householdsPending.value)
  const error = computed(() => actionError.value ?? householdsError.value?.message ?? null)

  // Helper to get current user ID
  const getUserId = (): string | null => userStore.user?.uid ?? null
  const getUserProfile = () => userStore.user

  // Getters
  const hasHouseholds = computed(() => households.value.length > 0)

  const currentHousehold = computed((): Household | null => {
    if (!currentHouseholdId.value) return null
    return households.value.find((h) => h.id === currentHouseholdId.value) ?? null
  })

  const currentHouseholdPantryIds = computed((): string[] => {
    return currentHousehold.value?.pantryIds ?? []
  })

  const currentHouseholdMealPlanId = computed((): string | null => {
    return currentHousehold.value?.mealPlanId ?? null
  })

  const isOwnerOfCurrentHousehold = computed((): boolean => {
    const uid = getUserId()
    if (!uid || !currentHousehold.value) return false
    return currentHousehold.value.ownerUid === uid
  })

  const userHouseholdIds = computed((): string[] => {
    return getUserProfile()?.households ?? []
  })

  /**
   * Get all pantry IDs that are in any of the user's households
   */
  const allHouseholdPantryIds = computed((): string[] => {
    const ids = new Set<string>()
    for (const household of households.value) {
      for (const pantryId of household.pantryIds ?? []) {
        ids.add(pantryId)
      }
    }
    return Array.from(ids)
  })

  /**
   * Check if a pantry is orphaned (not in any household)
   */
  const isPantryOrphaned = (pantryId: string): boolean => {
    return !allHouseholdPantryIds.value.includes(pantryId)
  }

  // Watch for changes to set current household automatically
  watch(
    [households, () => userStore.user?.current_household],
    ([newHouseholds, currentFromProfile]) => {
      // If current household is set and valid, keep it
      if (currentHouseholdId.value && newHouseholds.some(h => h.id === currentHouseholdId.value)) {
        return
      }
      // Try to use the one from user profile
      if (currentFromProfile && newHouseholds.some(h => h.id === currentFromProfile)) {
        currentHouseholdId.value = currentFromProfile
        return
      }
      // Fall back to first household
      if (newHouseholds.length > 0) {
        currentHouseholdId.value = newHouseholds[0].id ?? null
      } else {
        currentHouseholdId.value = null
      }
    },
    { immediate: true }
  )

  /**
   * Create a new household with a default pantry and meal plan
   */
  const createHousehold = async (name: string): Promise<string | null> => {
    const uid = getUserId()
    const user = getUserProfile()
    if (!uid || !user) {
      actionError.value = 'Must be logged in to create a household'
      return null
    }

    console.log('[HouseholdStore] Creating household for user:', uid, 'Current user profile:', {
      households: user.households,
      pantries: user.pantries
    })

    actionLoading.value = true
    actionError.value = null

    try {
      const batch = writeBatch(firestore)

      // Generate IDs
      const householdId = generateId()
      const pantryId = generateId()
      const mealPlanId = generateId()
      const inviteCode = generateInviteCode()

      // Create the default pantry
      const pantryRef = doc(firestore, 'pantries', pantryId)
      const pantryData: Partial<Pantry> = {
        name: 'Kitchen',
        member_count: 1,
        members: [
          {
            id: uid,
            name: user.name ?? null,
            email: user.email ?? null,
            image_url: user.image_url ?? null,
            role: 'owner'
          }
        ],
        pantryItems: []
      }
      batch.set(pantryRef, pantryData)

      // Create the pantry member doc for security rules
      const pantryMemberRef = doc(firestore, 'pantries', pantryId, 'members', uid)
      batch.set(pantryMemberRef, {
        id: uid,
        userId: uid,
        name: user.name ?? null,
        email: user.email ?? null,
        image_url: user.image_url ?? null,
        role: 'owner'
      })

      // Create the meal plan
      const mealPlanRef = doc(firestore, 'mealPlans', mealPlanId)
      const mealPlanData: Omit<MealPlan, 'id'> = {
        householdId,
        recurring: createEmptyRecurringPlan(),
        dateOverrides: {},
        created_at: serverTimestamp() as any,
        updated_at: serverTimestamp() as any
      }
      batch.set(mealPlanRef, mealPlanData)

      // Create the household
      const householdRef = doc(firestore, 'households', householdId)
      const householdData: Omit<Household, 'id'> = {
        name,
        ownerUid: uid,
        members: [
          {
            uid,
            name: user.name ?? null,
            email: user.email ?? null,
            image_url: user.image_url ?? null,
            role: 'owner',
            joined_at: new Date()  // Can't use serverTimestamp() inside arrays
          }
        ],
        memberIds: [uid], // For security rules
        pantryIds: [pantryId],
        mealPlanId,
        recipeBookIds: [],
        shoppingList: [],
        inviteCode,
        created_at: serverTimestamp() as any,
        updated_at: serverTimestamp() as any
      }
      batch.set(householdRef, householdData)

      // Update user profile with new household and pantry
      const userRef = doc(firestore, 'users', uid)
      batch.update(userRef, {
        households: arrayUnion(householdId),
        pantries: arrayUnion(pantryId),
        current_household: householdId,
        current_pantry: pantryId
      })

      await batch.commit()
      console.log('[HouseholdStore] Batch committed successfully:', { householdId, pantryId, mealPlanId })

      // Update local state
      households.value.push({ id: householdId, ...householdData } as Household)
      currentHouseholdId.value = householdId

      // VueFire in pantryStore will automatically pick up the new pantry
      // when user.pantries array is updated in Firestore

      return householdId
    } catch (e: any) {
      console.error('Error creating household:', e)
      console.error('Error details:', e?.code, e?.message, e?.stack)
      actionError.value = e?.message || 'Failed to create household'
      return null
    } finally {
      actionLoading.value = false
    }
  }

  /**
   * Join a household using an invite code
   */
  const joinHouseholdByInviteCode = async (inviteCode: string): Promise<boolean> => {
    const uid = getUserId()
    const user = getUserProfile()
    if (!uid || !user) {
      actionError.value = 'Must be logged in to join a household'
      return false
    }

    actionLoading.value = true
    actionError.value = null

    try {
      const joinHousehold = httpsCallable<
        { inviteCode: string },
        { householdId: string }
      >(functions, 'joinHouseholdByInviteCode')
      const result = await joinHousehold({ inviteCode: inviteCode.toUpperCase() })
      const householdId = result.data.householdId

      // Keep the active selection in sync immediately. The profile and
      // household query listeners may update on separate ticks.
      currentHouseholdId.value = householdId

      // VueFire in pantryStore will automatically pick up the household pantries
      // when user.pantries array is updated in Firestore

      // VueFire will automatically update households when user.households changes
      return true
    } catch (e) {
      console.error('Error joining household:', e)
      actionError.value = e instanceof Error ? e.message : 'Failed to join household'
      return false
    } finally {
      actionLoading.value = false
    }
  }

  /**
   * Get invite link for current household
   */
  const getInviteLink = computed((): string | null => {
    const inviteCode = currentHousehold.value?.inviteCode
    if (!inviteCode) return null
    return `${window.location.origin}/join/${inviteCode}`
  })

  /**
   * Remove a member from the household (owner only)
   */
  const removeMember = async (memberUid: string): Promise<boolean> => {
    const uid = getUserId()
    if (!isOwnerOfCurrentHousehold.value || !currentHousehold.value?.id) {
      actionError.value = 'Only the owner can remove members'
      return false
    }

    if (memberUid === uid) {
      actionError.value = 'Cannot remove yourself. Transfer ownership first.'
      return false
    }

    actionLoading.value = true
    actionError.value = null

    try {
      const householdId = currentHousehold.value.id
      const removeHouseholdMember = httpsCallable<
        { householdId: string; memberUid: string },
        void
      >(functions, 'removeHouseholdMember')
      await removeHouseholdMember({ householdId, memberUid })

      // VueFire will automatically update households
      return true
    } catch (e) {
      console.error('Error removing member:', e)
      actionError.value = 'Failed to remove member'
      return false
    } finally {
      actionLoading.value = false
    }
  }

  /**
   * Leave a household (for non-owners)
   */
  const leaveHousehold = async (householdId: string): Promise<boolean> => {
    const uid = getUserId()
    if (!uid) {
      actionError.value = 'Must be logged in'
      return false
    }

    const household = households.value.find((h) => h.id === householdId)
    if (!household) {
      actionError.value = 'Household not found'
      return false
    }

    if (household.ownerUid === uid) {
      actionError.value = 'Owner cannot leave. Transfer ownership or delete the household.'
      return false
    }

    actionLoading.value = true
    actionError.value = null

    try {
      const batch = writeBatch(firestore)
      const remainingHouseholdId = (userStore.user?.households ?? [])
        .find(id => id !== householdId) ?? null
      const remainingHousehold = remainingHouseholdId
        ? households.value.find(h => h.id === remainingHouseholdId)
        : null

      // Find and remove self from household
      const memberToRemove = household.members.find((m) => m.uid === uid)
      if (memberToRemove) {
        const householdRef = doc(firestore, 'households', householdId)
        batch.update(householdRef, {
          members: arrayRemove(memberToRemove),
          memberIds: arrayRemove(uid),
          updated_at: serverTimestamp()
        })
      }

      // Remove from all pantries
      for (const pantryId of household.pantryIds) {
        const pantryRef = doc(firestore, 'pantries', pantryId)
        const pantrySnap = await getDoc(pantryRef)
        if (pantrySnap.exists()) {
          const pantryData = pantrySnap.data()
          const pantryMember = pantryData.members?.find((m: any) => m.id === uid)
          if (pantryMember) {
            batch.update(pantryRef, {
              members: arrayRemove(pantryMember),
              member_count: Math.max(0, (pantryData.member_count ?? 1) - 1)
            })
          }
        }

        // Remove member doc
        const pantryMemberRef = doc(firestore, 'pantries', pantryId, 'members', uid)
        batch.delete(pantryMemberRef)
      }

      // Remove from user profile
      const userRef = doc(firestore, 'users', uid)
      batch.update(userRef, {
        households: arrayRemove(householdId),
        pantries: arrayRemove(...household.pantryIds),
        current_household: remainingHouseholdId,
        current_pantry: remainingHousehold?.pantryIds[0] ?? null
      })

      await batch.commit()

      // VueFire will automatically update when user.households changes
      // Reset current household if we left it
      if (currentHouseholdId.value === householdId) {
        currentHouseholdId.value = remainingHouseholdId
      }

      return true
    } catch (e) {
      console.error('Error leaving household:', e)
      actionError.value = 'Failed to leave household'
      return false
    } finally {
      actionLoading.value = false
    }
  }

  /**
   * Add an existing pantry to a household
   */
  const addPantryToHousehold = async (pantryId: string, householdId: string): Promise<boolean> => {
    const household = households.value.find((h) => h.id === householdId)
    if (!household) {
      actionError.value = 'Household not found'
      return false
    }

    actionLoading.value = true
    actionError.value = null

    try {
      const householdRef = doc(firestore, 'households', householdId)
      await updateDoc(householdRef, {
        pantryIds: arrayUnion(pantryId),
        updated_at: serverTimestamp()
      })

      // Update local state
      household.pantryIds.push(pantryId)

      return true
    } catch (e) {
      console.error('Error adding pantry to household:', e)
      actionError.value = 'Failed to add pantry to household'
      return false
    } finally {
      actionLoading.value = false
    }
  }

  /**
   * Create a new pantry within a household
   */
  const createPantryInHousehold = async (
    householdId: string,
    pantryName: string
  ): Promise<string | null> => {
    const uid = getUserId()
    const user = getUserProfile()
    const household = households.value.find((h) => h.id === householdId)

    if (!uid || !user || !household) {
      actionError.value = 'Invalid state'
      return null
    }

    actionLoading.value = true
    actionError.value = null

    try {
      const batch = writeBatch(firestore)
      const pantryId = generateId()

      // Create the pantry with all household members
      const pantryRef = doc(firestore, 'pantries', pantryId)
      const pantryData: Partial<Pantry> = {
        name: pantryName,
        member_count: household.members.length,
        members: household.members.map((m) => ({
          id: m.uid,
          name: m.name ?? null,
          email: m.email ?? null,
          image_url: m.image_url ?? null,
          role: m.role === 'owner' ? 'owner' : 'member'
        })),
        pantryItems: []
      }
      batch.set(pantryRef, pantryData)

      // Create member docs for security rules
      for (const member of household.members) {
        const pantryMemberRef = doc(firestore, 'pantries', pantryId, 'members', member.uid)
        batch.set(pantryMemberRef, {
          id: member.uid,
          userId: member.uid,
          name: member.name ?? null,
          email: member.email ?? null,
          image_url: member.image_url ?? null,
          role: member.role === 'owner' ? 'owner' : 'member'
        })
      }

      // Add pantry to household
      const householdRef = doc(firestore, 'households', householdId)
      batch.update(householdRef, {
        pantryIds: arrayUnion(pantryId),
        updated_at: serverTimestamp()
      })

      // Add pantry to all household members' profiles
      for (const member of household.members) {
        const memberRef = doc(firestore, 'users', member.uid)
        batch.update(memberRef, {
          pantries: arrayUnion(pantryId)
        })
      }

      await batch.commit()

      // Update local state
      household.pantryIds.push(pantryId)

      return pantryId
    } catch (e) {
      console.error('Error creating pantry:', e)
      actionError.value = 'Failed to create pantry'
      return null
    } finally {
      actionLoading.value = false
    }
  }

  /**
   * Set the current household
   */
  const setCurrentHousehold = async (householdId: string): Promise<void> => {
    const uid = getUserId()
    if (!uid) return

    currentHouseholdId.value = householdId

    // Also update the current pantry to the first pantry in this household
    const household = households.value.find((h) => h.id === householdId)
    const firstPantryId = household?.pantryIds[0] ?? null

    try {
      const userRef = doc(firestore, 'users', uid)
      await updateDoc(userRef, {
        current_household: householdId,
        current_pantry: firstPantryId
      })
    } catch (e) {
      console.error('Error updating current household:', e)
    }
  }

  /**
   * Update household name
   */
  const updateHouseholdName = async (householdId: string, name: string): Promise<boolean> => {
    actionLoading.value = true
    actionError.value = null

    try {
      const householdRef = doc(firestore, 'households', householdId)
      await updateDoc(householdRef, {
        name,
        updated_at: serverTimestamp()
      })

      // Update local state
      const household = households.value.find((h) => h.id === householdId)
      if (household) {
        household.name = name
      }

      return true
    } catch (e) {
      console.error('Error updating household name:', e)
      actionError.value = 'Failed to update household name'
      return false
    } finally {
      actionLoading.value = false
    }
  }

  /**
   * Regenerate invite code (owner only)
   */
  const regenerateInviteCode = async (): Promise<string | null> => {
    if (!isOwnerOfCurrentHousehold.value || !currentHousehold.value?.id) {
      actionError.value = 'Only the owner can regenerate the invite code'
      return null
    }

    actionLoading.value = true
    actionError.value = null

    try {
      const newCode = generateInviteCode()
      const householdRef = doc(firestore, 'households', currentHousehold.value.id)
      await updateDoc(householdRef, {
        inviteCode: newCode,
        updated_at: serverTimestamp()
      })

      // Update local state
      currentHousehold.value.inviteCode = newCode

      return newCode
    } catch (e) {
      console.error('Error regenerating invite code:', e)
      actionError.value = 'Failed to regenerate invite code'
      return null
    } finally {
      actionLoading.value = false
    }
  }

  // ==========================================
  // HOUSEHOLD DELETION & OWNERSHIP MANAGEMENT
  // ==========================================

  /**
   * Grace period in days before soft-deleted households are permanently removed
   */
  const DELETION_GRACE_PERIOD_DAYS = 90

  /**
   * Get households that are marked as deleted but still in grace period
   */
  const deletedHouseholds = computed(() => {
    return households.value.filter(h => h.deleted === true)
  })

  /**
   * Get active (non-deleted) households
   */
  const activeHouseholds = computed(() => {
    return households.value.filter(h => !h.deleted)
  })

  /**
   * Check if current user can claim ownership of a deleted household
   */
  const canClaimHousehold = (household: Household): boolean => {
    const uid = getUserId()
    if (!uid || !household.deleted) return false
    // User must be a member of the household
    return household.members.some(m => m.uid === uid)
  }

  /**
   * Get remaining days before a deleted household is permanently removed
   */
  const getDaysUntilPermanentDeletion = (household: Household): number | null => {
    if (!household.deleted || !household.deletedAt) return null

    const deletedDate = household.deletedAt.toDate()
    const expirationDate = new Date(deletedDate.getTime() + DELETION_GRACE_PERIOD_DAYS * 24 * 60 * 60 * 1000)
    const now = new Date()

    const remainingMs = expirationDate.getTime() - now.getTime()
    const remainingDays = Math.ceil(remainingMs / (24 * 60 * 60 * 1000))

    return Math.max(0, remainingDays)
  }

  /**
   * Soft delete a household (owner only)
   * The household will be marked as deleted and removed after grace period
   * During grace period, any member can claim ownership to restore it
   */
  const deleteHousehold = async (householdId: string): Promise<boolean> => {
    const uid = getUserId()
    if (!uid) {
      actionError.value = 'You must be logged in'
      return false
    }

    const household = households.value.find(h => h.id === householdId)
    if (!household) {
      actionError.value = 'Household not found'
      return false
    }

    if (household.ownerUid !== uid) {
      actionError.value = 'Only the owner can delete a household'
      return false
    }

    actionLoading.value = true
    actionError.value = null

    try {
      const householdRef = doc(firestore, 'households', householdId)
      await updateDoc(householdRef, {
        deleted: true,
        deletedAt: serverTimestamp(),
        deletedBy: uid,
        updated_at: serverTimestamp()
      })

      // If this was the current household, clear selection
      if (currentHouseholdId.value === householdId) {
        // Try to select another active household
        const otherHousehold = activeHouseholds.value.find(h => h.id !== householdId)
        currentHouseholdId.value = otherHousehold?.id || null
      }

      return true
    } catch (e) {
      console.error('Error deleting household:', e)
      actionError.value = 'Failed to delete household'
      return false
    } finally {
      actionLoading.value = false
    }
  }

  /**
   * Restore a soft-deleted household by claiming ownership
   * Any member can claim ownership during grace period
   */
  const claimHouseholdOwnership = async (householdId: string): Promise<boolean> => {
    const uid = getUserId()
    if (!uid) {
      actionError.value = 'You must be logged in'
      return false
    }

    const household = households.value.find(h => h.id === householdId)
    if (!household) {
      actionError.value = 'Household not found'
      return false
    }

    if (!household.deleted) {
      actionError.value = 'This household is not pending deletion'
      return false
    }

    // Check if user is a member
    if (!household.members.some(m => m.uid === uid)) {
      actionError.value = 'You must be a member to claim ownership'
      return false
    }

    actionLoading.value = true
    actionError.value = null

    try {
      // Update the owner and remove deletion status
      const updatedMembers = household.members.map(m => ({
        ...m,
        role: m.uid === uid ? 'owner' as const : 'member' as const
      }))

      const householdRef = doc(firestore, 'households', householdId)
      await updateDoc(householdRef, {
        ownerUid: uid,
        members: updatedMembers,
        deleted: deleteField(),
        deletedAt: deleteField(),
        deletedBy: deleteField(),
        updated_at: serverTimestamp()
      })

      return true
    } catch (e) {
      console.error('Error claiming household ownership:', e)
      actionError.value = 'Failed to claim household ownership'
      return false
    } finally {
      actionLoading.value = false
    }
  }

  /**
   * Transfer ownership of a household to another member (owner only)
   */
  const transferOwnership = async (householdId: string, newOwnerUid: string): Promise<boolean> => {
    const uid = getUserId()
    if (!uid) {
      actionError.value = 'You must be logged in'
      return false
    }

    const household = households.value.find(h => h.id === householdId)
    if (!household) {
      actionError.value = 'Household not found'
      return false
    }

    if (household.ownerUid !== uid) {
      actionError.value = 'Only the owner can transfer ownership'
      return false
    }

    if (household.deleted) {
      actionError.value = 'Cannot transfer ownership of a deleted household'
      return false
    }

    // Check if new owner is a member
    if (!household.members.some(m => m.uid === newOwnerUid)) {
      actionError.value = 'New owner must be a member of the household'
      return false
    }

    if (newOwnerUid === uid) {
      actionError.value = 'You are already the owner'
      return false
    }

    actionLoading.value = true
    actionError.value = null

    try {
      // Update member roles
      const updatedMembers = household.members.map(m => ({
        ...m,
        role: m.uid === newOwnerUid ? 'owner' as const : 'member' as const
      }))

      const householdRef = doc(firestore, 'households', householdId)
      await updateDoc(householdRef, {
        ownerUid: newOwnerUid,
        members: updatedMembers,
        updated_at: serverTimestamp()
      })

      return true
    } catch (e) {
      console.error('Error transferring ownership:', e)
      actionError.value = 'Failed to transfer ownership'
      return false
    } finally {
      actionLoading.value = false
    }
  }

  /**
   * Cancel deletion of a household (owner or member who initiated deletion)
   */
  const cancelHouseholdDeletion = async (householdId: string): Promise<boolean> => {
    const uid = getUserId()
    if (!uid) {
      actionError.value = 'You must be logged in'
      return false
    }

    const household = households.value.find(h => h.id === householdId)
    if (!household) {
      actionError.value = 'Household not found'
      return false
    }

    if (!household.deleted) {
      actionError.value = 'Household is not pending deletion'
      return false
    }

    // Only owner or the person who deleted it can cancel
    if (household.ownerUid !== uid && household.deletedBy !== uid) {
      actionError.value = 'Only the owner or person who initiated deletion can cancel'
      return false
    }

    actionLoading.value = true
    actionError.value = null

    try {
      const householdRef = doc(firestore, 'households', householdId)
      await updateDoc(householdRef, {
        deleted: deleteField(),
        deletedAt: deleteField(),
        deletedBy: deleteField(),
        updated_at: serverTimestamp()
      })

      return true
    } catch (e) {
      console.error('Error canceling household deletion:', e)
      actionError.value = 'Failed to cancel deletion'
      return false
    } finally {
      actionLoading.value = false
    }
  }

  // ==========================================
  // HOUSEHOLD SHOPPING LIST
  // ==========================================

  // Get shopping list for current household
  const currentHouseholdShoppingList = computed<ShoppingListItem[]>(() => {
    return currentHousehold.value?.shoppingList ?? []
  })

  // Get shopping list for a specific household
  const getHouseholdShoppingList = (householdId: string): ShoppingListItem[] => {
    const household = households.value.find(h => h.id === householdId)
    return household?.shoppingList ?? []
  }

  // Add item to household shopping list
  const addItemToHouseholdShoppingList = async (householdId: string, item: ShoppingListItem) => {
    if (!item?.name) return

    const household = households.value.find(h => h.id === householdId)
    if (!household) {
      console.error('Household not found:', householdId)
      return
    }

    const currentList = household.shoppingList ?? []
    const exists = currentList.some(
      (existing) => existing.name.toLowerCase() === item.name.toLowerCase()
    )
    if (exists) return

    const newItem: ShoppingListItem = {
      name: item.name,
      checked: false,
      ...(item.measurements && { measurements: item.measurements })
    }

    try {
      const householdRef = doc(firestore, 'households', householdId)
      await updateDoc(householdRef, {
        shoppingList: [...currentList, newItem],
        updated_at: serverTimestamp()
      })
    } catch (error) {
      console.error('Failed to add item to household shopping list:', error)
    }
  }

  // Remove items from household shopping list
  const removeItemsFromHouseholdShoppingList = async (householdId: string, selectedItems: Record<string, boolean>) => {
    const household = households.value.find(h => h.id === householdId)
    if (!household) return

    const currentList = household.shoppingList ?? []
    const newList = currentList.filter((item) => !selectedItems[item.name])

    try {
      const householdRef = doc(firestore, 'households', householdId)
      await updateDoc(householdRef, {
        shoppingList: newList,
        updated_at: serverTimestamp()
      })
    } catch (error) {
      console.error('Failed to remove items from household shopping list:', error)
    }
  }

  // Toggle checked state of shopping list item
  const toggleHouseholdShoppingListItemChecked = async (householdId: string, itemName: string) => {
    const household = households.value.find(h => h.id === householdId)
    if (!household) return

    const currentList = household.shoppingList ?? []
    const newList = currentList.map((item) => {
      if (item.name === itemName) {
        return { ...item, checked: !item.checked }
      }
      return item
    })

    try {
      const householdRef = doc(firestore, 'households', householdId)
      await updateDoc(householdRef, {
        shoppingList: newList,
        updated_at: serverTimestamp()
      })
    } catch (error) {
      console.error('Failed to toggle household shopping list item:', error)
    }
  }

  // Clear checked items from household shopping list
  const clearCheckedHouseholdShoppingListItems = async (householdId: string) => {
    const household = households.value.find(h => h.id === householdId)
    if (!household) return

    const currentList = household.shoppingList ?? []
    const newList = currentList.filter((item) => !item.checked)

    try {
      const householdRef = doc(firestore, 'households', householdId)
      await updateDoc(householdRef, {
        shoppingList: newList,
        updated_at: serverTimestamp()
      })
    } catch (error) {
      console.error('Failed to clear checked items from household shopping list:', error)
    }
  }

  return {
    // State (VueFire reactive)
    households,
    currentHouseholdId,
    loading,
    error,

    // Getters
    hasHouseholds,
    currentHousehold,
    currentHouseholdPantryIds,
    currentHouseholdMealPlanId,
    isOwnerOfCurrentHousehold,
    userHouseholdIds,
    allHouseholdPantryIds,
    isPantryOrphaned,
    getInviteLink,
    deletedHouseholds,
    activeHouseholds,

    // Actions
    createHousehold,
    joinHouseholdByInviteCode,
    removeMember,
    leaveHousehold,
    addPantryToHousehold,
    createPantryInHousehold,
    setCurrentHousehold,
    updateHouseholdName,
    regenerateInviteCode,

    // Deletion & Ownership
    deleteHousehold,
    claimHouseholdOwnership,
    transferOwnership,

    // Shopping List
    currentHouseholdShoppingList,
    getHouseholdShoppingList,
    addItemToHouseholdShoppingList,
    removeItemsFromHouseholdShoppingList,
    toggleHouseholdShoppingListItemChecked,
    clearCheckedHouseholdShoppingListItems,
    cancelHouseholdDeletion,
    canClaimHousehold,
    getDaysUntilPermanentDeletion,
    DELETION_GRACE_PERIOD_DAYS
  }
})
